import React from 'react';

import { IonContent, IonIcon, IonPopover } from '@ionic/react';
import { arrowDown, arrowUp } from 'ionicons/icons';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';

import ProgressBar from 'components/ProgressBar';

import Pagination from './components/Pagination';
import './index.css';
import './sticky.css';
import './summary-row.css';

interface IDataTable {
  data: any;
  columns: any;
  isRefetching?: boolean;
  sticky?: boolean;
  firstRowSummary?: boolean;
  showFooter?: boolean;
  paging?: any;
  tableSettings?: any;
  onChangePagination?: any;
  onChangeSort?: any;
  className?: string;
  fullWidth?: boolean;
}
const DataTable = ({
  data,
  paging,
  columns,
  isRefetching = false,
  sticky = false,
  firstRowSummary = false,
  showFooter = false,
  fullWidth = false,
  onChangePagination,
  onChangeSort,
  tableSettings,
  className,
}: IDataTable) => {
  const table = useReactTable({
    data: data || [],
    columns: columns || [],
    getCoreRowModel: getCoreRowModel(),
  });

  if (!data.length) {
    return null;
  }
  return (
    <div
      data-cy={'DataTable'}
      className={clsx('datatable', className, {
        'first-row-summary': firstRowSummary,
        sticky: sticky,
        fullwidth: fullWidth,
      })}
    >
      {isRefetching && <ProgressBar type={'indeterminate'} />}
      <div className={'table-container'}>
        <table>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  if (!header.column.columnDef.header) {
                    return null;
                  }

                  const { name, id, colSpan }: any =
                    header.column.columnDef.header;

                  const triggerId = `click-trigger-${
                    name.replaceAll?.(' ', '-') || id
                  }`;

                  return (
                    <th key={header.id} colSpan={colSpan}>
                      <div>
                        <span id={triggerId}>
                          {flexRender(name, header.getContext())}
                        </span>
                        <IonPopover
                          trigger={triggerId}
                          triggerAction={'click'}
                          side={'bottom'}
                          alignment={'center'}
                        >
                          <IonContent class={'ion-padding'}>{name}</IonContent>
                        </IonPopover>
                        {!!tableSettings && (
                          <div className={'sort-icon-container'}>
                            <IonIcon
                              className={clsx({
                                'datatable-sort-icon': true,
                                'datatable-sort-icon-current':
                                  tableSettings.orderBy === id,
                              })}
                              icon={
                                tableSettings.orderBy === id
                                  ? tableSettings.orderDir === 'desc'
                                    ? arrowDown
                                    : arrowUp
                                  : arrowDown
                              }
                              onClick={onChangeSort(id)}
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    <span>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {showFooter && (
            <tfoot>
              {table.getFooterGroups().map(footerGroup => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map(header => {
                    return (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext()
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
      {!!tableSettings && (
        <Pagination
          count={paging.count}
          limit={tableSettings.limit}
          offset={tableSettings.offset}
          onChange={onChangePagination}
        />
      )}
    </div>
  );
};
export default DataTable;
